import { LayoutHeader } from "@/Header";
import { useState, useEffect, useRef } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { format } from "date-fns";
import { CalendarNew } from "@/CalendarNew";
import {
  useBusinessMypageBusinessTime,
  useBusinessMypageBusinessTimeUpdate,
  useBusinessMypageHolidays,
  useBusinessMypageHolidaySave,
  useBusinessMypageHolidayDelete,
  useBusinessMypageReserveDeadline,
  useBusinessMypageReserveDeadlineSave,
} from "../../../api/services/business/myPage";
import { BusinessMypageBusinessTimeRequest } from "../../../api/types/common";

interface BusinessTimeData {
  businessTimeSeq: number;
  companySeq: number;
  cdDayOfWeek: string;
  startTime: string;
  endTime: string;
  close: boolean;
}

interface DaySchedule {
  day: string;
  label: string;
  isActive: boolean;
  startHour: string;
  startMinute: string;
  endHour: string;
  endMinute: string;
  businessTimeSeq?: number; // API에서 받아온 값 저장
  companySeq?: number; // API에서 받아온 값 저장
}

const DAYS = [
  { day: "MONDAY", label: "월요일" },
  { day: "TUESDAY", label: "화요일" },
  { day: "WEDNESDAY", label: "수요일" },
  { day: "THURSDAY", label: "목요일" },
  { day: "FRIDAY", label: "금요일" },
  { day: "SATURDAY", label: "토요일" },
  { day: "SUNDAY", label: "일요일" },
];

const HOUR_OPTIONS = [
  { value: "01", label: "오전 1시" },
  { value: "02", label: "오전 2시" },
  { value: "03", label: "오전 3시" },
  { value: "04", label: "오전 4시" },
  { value: "05", label: "오전 5시" },
  { value: "06", label: "오전 6시" },
  { value: "07", label: "오전 7시" },
  { value: "08", label: "오전 8시" },
  { value: "09", label: "오전 9시" },
  { value: "10", label: "오전 10시" },
  { value: "11", label: "오전 11시" },
  { value: "12", label: "오후 12시" },
  { value: "13", label: "오후 1시" },
  { value: "14", label: "오후 2시" },
  { value: "15", label: "오후 3시" },
  { value: "16", label: "오후 4시" },
  { value: "17", label: "오후 5시" },
  { value: "18", label: "오후 6시" },
  { value: "19", label: "오후 7시" },
  { value: "20", label: "오후 8시" },
  { value: "21", label: "오후 9시" },
  { value: "22", label: "오후 10시" },
  { value: "23", label: "오후 11시" },
  { value: "00", label: "오전 12시" },
];

const MINUTE_OPTIONS = [
  { value: "00", label: "00분" },
  { value: "15", label: "15분" },
  { value: "30", label: "30분" },
  { value: "45", label: "45분" },
];

// 커스텀 토글 스위치 컴포넌트
const ToggleSwitch = ({
  checked,
  onChange,
  disabled = false,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}) => {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`
        relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2
        ${checked ? "bg-orange-400" : "bg-[#CCCCCC]"}
        ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
      `}
    >
      <span
        className={`
          inline-block h-4 w-4 transform rounded-full bg-white transition-transform
          ${checked ? "translate-x-6" : "translate-x-1"}
        `}
      />
    </button>
  );
};

// 커스텀 드롭다운 컴포넌트
const CustomSelect = ({
  value,
  onChange,
  options,
  disabled = false,
  className = "",
  filteredOptions, // 필터링된 옵션을 받을 수 있도록 추가
}: {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  disabled?: boolean;
  className?: string;
  filteredOptions?: { value: string; label: string }[]; // 필터링된 옵션
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // 필터링된 옵션이 있으면 사용하고, 없으면 원본 옵션 사용
  const displayOptions = filteredOptions || options;
  const selectedOption = displayOptions.find(
    (option) => option.value === value
  );

  // 디버깅을 위한 로그
  console.log("CustomSelect debug:", { value, options, selectedOption });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          w-full flex items-center justify-between px-3 py-2 text-sm border border-gray-300 rounded-md bg-white
          focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400
          ${disabled ? "text-gray-500 bg-gray-100 cursor-not-allowed" : "cursor-pointer hover:border-gray-400"}
        `}
      >
        <span>{selectedOption?.label || "선택하세요"}</span>
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && !disabled && (
        <div className="overflow-y-auto absolute z-10 mt-1 w-full max-h-48 bg-white rounded-md border border-gray-300 shadow-lg">
          {displayOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleSelect(option.value)}
              className={`
                w-full px-3 py-2 text-sm text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none
                ${option.value === value ? "bg-orange-400 text-white hover:bg-blue-600" : "text-gray-900"}
              `}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// 요일별 영업시간 카드 컴포넌트
const DayScheduleCard = ({
  schedule,
  onToggle,
  onTimeChange,
}: {
  schedule: DaySchedule;
  onToggle: (day: string, isActive: boolean) => void;
  onTimeChange: (day: string, field: string, value: string) => void;
}) => {
  // 종료시간 옵션 필터링: 시작시간보다 늦은 시간만 선택 가능
  const filteredEndHourOptions = HOUR_OPTIONS.filter(
    (hour) => hour.value > schedule.startHour
  );

  // 종료시간이 시작시간과 같거나 이전인 경우, 시작시간 다음 시간으로 자동 조정
  const handleEndHourChange = (value: string) => {
    if (value <= schedule.startHour) {
      // 시작시간보다 작거나 같은 시간을 선택하려고 할 때, 시작시간 다음 시간으로 설정
      const currentHourIndex = HOUR_OPTIONS.findIndex(
        (h) => h.value === schedule.startHour
      );
      const nextHour = HOUR_OPTIONS[currentHourIndex + 1] || HOUR_OPTIONS[0];
      onTimeChange(schedule.day, "endHour", nextHour.value);
    } else {
      onTimeChange(schedule.day, "endHour", value);
    }
  };

  return (
    <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          {schedule.label}
        </h3>
        <ToggleSwitch
          checked={schedule.isActive}
          onChange={(checked) => onToggle(schedule.day, checked)}
        />
      </div>

      <div className="space-y-4">
        {/* 시작 시간 */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <label className="flex-shrink-0 w-16 text-sm font-medium text-gray-700">
            시작
          </label>
          <div className="flex flex-1 gap-2">
            <div className="flex-1 min-w-0">
              <CustomSelect
                value={schedule.startHour}
                onChange={(value) =>
                  onTimeChange(schedule.day, "startHour", value)
                }
                options={HOUR_OPTIONS}
                disabled={!schedule.isActive}
                className="w-full text-sm"
              />
            </div>
            <div className="flex-shrink-0 w-20">
              <CustomSelect
                value={schedule.startMinute}
                onChange={(value) =>
                  onTimeChange(schedule.day, "startMinute", value)
                }
                options={MINUTE_OPTIONS}
                disabled={!schedule.isActive}
                className="w-full text-sm"
              />
            </div>
          </div>
        </div>

        {/* 종료 시간 */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <label className="flex-shrink-0 w-16 text-sm font-medium text-gray-700">
            종료
          </label>
          <div className="flex flex-1 gap-2">
            <div className="flex-1 min-w-0">
              <CustomSelect
                value={schedule.endHour}
                onChange={handleEndHourChange}
                options={HOUR_OPTIONS}
                disabled={!schedule.isActive}
                className="w-full text-sm"
                filteredOptions={filteredEndHourOptions}
              />
            </div>
            <div className="flex-shrink-0 w-20">
              <CustomSelect
                value={schedule.endMinute}
                onChange={(value) =>
                  onTimeChange(schedule.day, "endMinute", value)
                }
                options={MINUTE_OPTIONS}
                disabled={!schedule.isActive}
                className="w-full text-sm"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const Route = createFileRoute(
  "/business/layout/user-business-time-management"
)({
  component: RouteComponent,
});

// "yyyy-MM-dd" → 로컬 타임존 Date (new Date(string)의 UTC 해석 회피)
const parseLocalDate = (dateStr: string) => {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d);
};

type Tab = "holiday" | "time" | "deadline";

const TabButton = ({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex-1 py-3 text-[14px] font-semibold transition-colors ${
      active
        ? "text-[#FF9934] border-b-2 border-[#FF9934]"
        : "text-[#999] border-b-2 border-transparent"
    }`}
  >
    {label}
  </button>
);

function RouteComponent() {
  const { data: businessTimeData, isLoading } = useBusinessMypageBusinessTime();
  const updateBusinessTimeMutation = useBusinessMypageBusinessTimeUpdate();
  const [schedules, setSchedules] = useState<DaySchedule[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [tab, setTab] = useState<Tab>("time");

  // 임시 휴무일
  const { data: holidayData } = useBusinessMypageHolidays();
  const saveHolidayMutation = useBusinessMypageHolidaySave();
  const deleteHolidayMutation = useBusinessMypageHolidayDelete();
  const [selectedHolidayDates, setSelectedHolidayDates] = useState<Date[]>([]);
  const [holidayMemo, setHolidayMemo] = useState("");

  const existingHolidays = holidayData?.data ?? [];
  const existingHolidayDates = existingHolidays.map((h) =>
    parseLocalDate(h.holidayDate)
  );

  const handleHolidaySave = async () => {
    if (selectedHolidayDates.length === 0) {
      alert("휴무일로 지정할 날짜를 선택해 주세요.");
      return;
    }

    try {
      const trimmedMemo = holidayMemo.trim();
      const res = await saveHolidayMutation.mutateAsync({
        dates: selectedHolidayDates.map((d) => format(d, "yyyy-MM-dd")),
        memo: trimmedMemo || undefined,
      });

      const withReservations = (res.data ?? []).filter(
        (h) => h.reservationCount > 0
      );
      if (withReservations.length > 0) {
        const dateList = withReservations
          .map(
            (h) =>
              `${format(parseLocalDate(h.holidayDate), "yyyy.MM.dd")} (예약 ${h.reservationCount}건)`
          )
          .join("\n");
        alert(
          `휴무일이 저장되었습니다.\n\n아래 날짜에는 이미 접수/확정된 예약이 있습니다.\n해당 예약은 자동으로 취소되지 않으니 개별 확인해 주세요.\n\n${dateList}`
        );
      } else {
        alert("휴무일이 저장되었습니다.");
      }
      setSelectedHolidayDates([]);
      setHolidayMemo("");
    } catch (error) {
      console.error("휴무일 저장 실패:", error);
      alert("휴무일 저장에 실패했습니다. 다시 시도해주세요.");
    }
  };

  const handleHolidayDelete = async (holidaySeq: number, dateStr: string) => {
    if (
      !window.confirm(
        `${format(parseLocalDate(dateStr), "yyyy.MM.dd")} 휴무일을 해제하시겠습니까?`
      )
    ) {
      return;
    }
    try {
      await deleteHolidayMutation.mutateAsync(holidaySeq);
    } catch (error) {
      console.error("휴무일 해제 실패:", error);
      alert("휴무일 해제에 실패했습니다. 다시 시도해주세요.");
    }
  };

  // 예약 마감시간 설정
  const { data: deadlineData } = useBusinessMypageReserveDeadline();
  const saveDeadlineMutation = useBusinessMypageReserveDeadlineSave();
  const [deadlineEnabled, setDeadlineEnabled] = useState(false);
  const [deadlineHour, setDeadlineHour] = useState("21");
  const [deadlineMinute, setDeadlineMinute] = useState("00");

  // API 데이터를 로컬 상태로 반영
  useEffect(() => {
    if (deadlineData?.data) {
      setDeadlineEnabled(deadlineData.data.enabled);
      if (deadlineData.data.deadlineTime) {
        const [h, m] = deadlineData.data.deadlineTime.split(":");
        if (h) setDeadlineHour(h);
        if (m) setDeadlineMinute(m);
      }
    }
  }, [deadlineData]);

  const handleDeadlineSave = async () => {
    try {
      await saveDeadlineMutation.mutateAsync({
        enabled: deadlineEnabled,
        deadlineTime: deadlineEnabled
          ? `${deadlineHour}:${deadlineMinute}:00`
          : null,
      });
      alert("예약 마감시간 설정이 저장되었습니다.");
    } catch (error) {
      console.error("예약 마감시간 저장 실패:", error);
      alert("저장에 실패했습니다. 다시 시도해주세요.");
    }
  };

  // API 데이터를 로컬 상태로 변환
  useEffect(() => {
    if (businessTimeData?.data) {
      const apiDataMap = new Map(
        businessTimeData.data.map((item: BusinessTimeData) => [
          item.cdDayOfWeek,
          item,
        ])
      );

      const newSchedules = DAYS.map(({ day, label }) => {
        const apiData = apiDataMap.get(day);
        if (apiData) {
          console.log("API data for", day, ":", apiData);

          // 시간 파싱을 더 안전하게 처리
          const startTimeParts = apiData.startTime.split(":");
          const endTimeParts = apiData.endTime.split(":");

          const startHour = startTimeParts[0] || "09";
          const startMinute = startTimeParts[1] || "00";
          const endHour = endTimeParts[0] || "18";
          const endMinute = endTimeParts[1] || "00";

          console.log("Parsed time for", day, ":", {
            startHour,
            startMinute,
            endHour,
            endMinute,
            close: apiData.close,
          });

          return {
            day,
            label,
            isActive: !apiData.close, // close가 true면 isActive는 false
            startHour,
            startMinute,
            endHour,
            endMinute,
            businessTimeSeq: apiData.businessTimeSeq,
            companySeq: apiData.companySeq,
          };
        } else {
          // API 데이터가 없는 경우 기본값 (토요일, 일요일도 포함)
          console.log("No API data for", day, "- using default values");
          return {
            day,
            label,
            isActive: true, // 기본적으로 활성화
            startHour: "09",
            startMinute: "00",
            endHour: "18",
            endMinute: "00",
          };
        }
      });

      setSchedules(newSchedules);
    } else {
      // API 데이터가 아예 없는 경우 모든 요일에 기본값 설정
      console.log(
        "No API data available - setting default values for all days"
      );
      const defaultSchedules = DAYS.map(({ day, label }) => ({
        day,
        label,
        isActive: true,
        startHour: "09",
        startMinute: "00",
        endHour: "18",
        endMinute: "00",
      }));
      setSchedules(defaultSchedules);
    }
  }, [businessTimeData]);

  const handleToggle = (day: string, isActive: boolean) => {
    setSchedules((prev) =>
      prev.map((schedule) =>
        schedule.day === day ? { ...schedule, isActive } : schedule
      )
    );
  };

  const handleTimeChange = (day: string, field: string, value: string) => {
    setSchedules((prev) =>
      prev.map((schedule) =>
        schedule.day === day ? { ...schedule, [field]: value } : schedule
      )
    );
  };

  const handleSave = async () => {
    if (isSaving) return;

    setIsSaving(true);

    try {
      // API 호출을 위한 데이터 변환
      const saveData: BusinessMypageBusinessTimeRequest[] = schedules
        .filter((schedule) => schedule.businessTimeSeq && schedule.companySeq) // 필수 값이 있는 경우만 필터링
        .map((schedule) => ({
          businessTimeSeq: schedule.businessTimeSeq!,
          companySeq: schedule.companySeq!,
          cdDayOfWeek: schedule.day,
          startTime: `${schedule.startHour}:${schedule.startMinute}:00`,
          endTime: `${schedule.endHour}:${schedule.endMinute}:00`,
          close: !schedule.isActive,
        }));

      console.log("저장할 데이터:", saveData);

      if (saveData.length === 0) {
        alert("저장할 데이터가 없습니다.");
        return;
      }

      await updateBusinessTimeMutation.mutateAsync(saveData);

      // 성공 메시지
      alert("영업시간이 성공적으로 저장되었습니다.");
    } catch (error) {
      console.error("저장 실패:", error);
      // 실패 메시지
      alert("저장에 실패했습니다. 다시 시도해주세요.");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex-1 min-h-0 overflow-y-auto bg-gray-50">
        <LayoutHeader title="영업시간 관리" />
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-500">로딩 중...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0 bg-gray-50">
      <LayoutHeader title="영업시간 관리" hasBackBtn />

      {/* 탭 헤더 — 고정 영역 (휴일 지정 / 영업시간 / 예약 마감시간) */}
      <div className="flex bg-white border-b border-[#E9E9E9] shrink-0">
        <TabButton
          active={tab === "holiday"}
          label="휴일 지정"
          onClick={() => setTab("holiday")}
        />
        <TabButton
          active={tab === "time"}
          label="영업시간"
          onClick={() => setTab("time")}
        />
        <TabButton
          active={tab === "deadline"}
          label="예약 마감시간"
          onClick={() => setTab("deadline")}
        />
      </div>

      {/* 스크롤 영역 */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {tab === "time" && (
          <div className="p-4">
            <div className="space-y-4">
              {schedules.map((schedule) => (
                <DayScheduleCard
                  key={schedule.day}
                  schedule={schedule}
                  onToggle={handleToggle}
                  onTimeChange={handleTimeChange}
                />
              ))}
            </div>

            <div className="mt-6">
              <button
                onClick={handleSave}
                disabled={isSaving || updateBusinessTimeMutation.isPending}
                className="px-4 py-3 w-full text-base text-white bg-orange-400 rounded-full transition-colors hover:bg-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {isSaving || updateBusinessTimeMutation.isPending
                  ? "저장 중..."
                  : "저장"}
              </button>
            </div>
          </div>
        )}

        {tab === "holiday" && (
          <div className="p-4">
            <p className="mb-4 text-sm text-gray-500">
              정기 휴무 외에 쉬는 날짜를 지정하면 해당 날짜는 예약이
              불가합니다. 날짜를 여러 개 선택할 수 있습니다.
            </p>

            <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
              <CalendarNew
                multiSelect
                disablePast
                selectedDays={selectedHolidayDates}
                readOnlyDates={existingHolidayDates}
                disabledDates={existingHolidayDates}
                onChange={(days) => {
                  if (Array.isArray(days)) setSelectedHolidayDates(days);
                }}
              />
            </div>

            {selectedHolidayDates.length > 0 && (
              <p className="mt-2 text-sm text-gray-600">
                선택한 날짜:{" "}
                {[...selectedHolidayDates]
                  .sort((a, b) => a.getTime() - b.getTime())
                  .map((d) => format(d, "MM.dd"))
                  .join(", ")}
              </p>
            )}

            {/* 휴무 사유 (선택, 선택한 날짜 전체에 공통 적용) */}
            <div className="mt-4">
              <label className="block mb-1 text-sm font-medium text-gray-700">
                휴무 사유 (선택)
              </label>
              <input
                type="text"
                value={holidayMemo}
                onChange={(e) => setHolidayMemo(e.target.value)}
                maxLength={200}
                placeholder="예) 내부 공사, 직원 워크샵"
                className="px-3 py-2 w-full text-sm bg-white rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
              />
            </div>

            <div className="mt-4">
              <button
                onClick={handleHolidaySave}
                disabled={
                  saveHolidayMutation.isPending ||
                  selectedHolidayDates.length === 0
                }
                className="px-4 py-3 w-full text-base text-white bg-orange-400 rounded-full transition-colors hover:bg-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {saveHolidayMutation.isPending ? "저장 중..." : "휴무일 저장"}
              </button>
            </div>

            {existingHolidays.length > 0 && (
              <div className="mt-6">
                <h3 className="mb-2 text-base font-semibold text-gray-900">
                  지정된 휴무일
                </h3>
                <div className="space-y-2">
                  {existingHolidays.map((holiday) => (
                    <div
                      key={holiday.holidaySeq}
                      className="flex justify-between items-center p-3 bg-white rounded-lg border border-gray-200"
                    >
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {format(
                            parseLocalDate(holiday.holidayDate),
                            "yyyy.MM.dd"
                          )}
                        </p>
                        {holiday.memo && (
                          <p className="text-xs text-gray-500">
                            {holiday.memo}
                          </p>
                        )}
                        {holiday.reservationCount > 0 && (
                          <p className="text-xs text-red-500">
                            이 날짜에 접수/확정된 예약{" "}
                            {holiday.reservationCount}건이 있습니다.
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() =>
                          handleHolidayDelete(
                            holiday.holidaySeq,
                            holiday.holidayDate
                          )
                        }
                        disabled={deleteHolidayMutation.isPending}
                        className="px-3 py-1.5 text-sm text-gray-600 rounded-md border border-gray-300 hover:bg-gray-100 disabled:opacity-50"
                      >
                        해제
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "deadline" && (
          <div className="p-4">
            {/* 설명 문구 (필수) */}
            <div className="p-4 mb-4 text-sm leading-relaxed text-gray-600 bg-white rounded-lg border border-gray-200">
              <p>
                예약 마감 시간 설정은 예약 접수 가능 시간을 지정하여 다음날
                예약을 시스템에서 제한할 수 있는 기능입니다.
              </p>
              <p className="mt-2">
                예약 마감 시간 이후 고객 예약이 이루어질 때 예약을 시도하는
                날짜의 다음날은 예약 날짜로 선택할 수 없도록 변경됩니다.
              </p>
              <p className="mt-2">
                (예시) 1월 1일에 예약 마감시간 이후 고객 예약을 시도할 경우 1월
                3일부터 날짜 선택이 가능해집니다.
              </p>
              <p className="mt-2 text-orange-500">
                설정을 활성화하면 당일 예약은 마감시간과 관계없이 항상
                불가합니다.
              </p>
            </div>

            <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
              <div className="flex justify-between items-center">
                <h3 className="text-base font-semibold text-gray-900">
                  예약 마감시간 사용
                </h3>
                <ToggleSwitch
                  checked={deadlineEnabled}
                  onChange={setDeadlineEnabled}
                />
              </div>

              <div className="flex flex-col gap-2 mt-4 sm:flex-row sm:items-center">
                <label className="flex-shrink-0 w-16 text-sm font-medium text-gray-700">
                  마감시간
                </label>
                <div className="flex flex-1 gap-2">
                  <div className="flex-1 min-w-0">
                    <CustomSelect
                      value={deadlineHour}
                      onChange={setDeadlineHour}
                      options={HOUR_OPTIONS}
                      disabled={!deadlineEnabled}
                      className="w-full text-sm"
                    />
                  </div>
                  <div className="flex-shrink-0 w-20">
                    <CustomSelect
                      value={deadlineMinute}
                      onChange={setDeadlineMinute}
                      options={MINUTE_OPTIONS}
                      disabled={!deadlineEnabled}
                      className="w-full text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6">
              <button
                onClick={handleDeadlineSave}
                disabled={saveDeadlineMutation.isPending}
                className="px-4 py-3 w-full text-base text-white bg-orange-400 rounded-full transition-colors hover:bg-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {saveDeadlineMutation.isPending ? "저장 중..." : "저장"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
